import { useAtom } from '@reatom/npm-react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { Numeric } from '@blade/ui-kit'

import type { LimitOrderModel } from '../../atoms'
import type { LimitOrderBuyModel } from '../../atoms/reatom-limit-order-buy'
import type { LimitOrderSellModel } from '../../atoms/reatom-limit-order-sell'
import type { LimitOrder } from '../../model'

type Props = {
  order: LimitOrderModel
  showSizeEquivalent?: boolean
}

export const LimitOrderSize: React.FC<Props> = ({
  order,
  showSizeEquivalent = true,
}) => {
  const { t } = useTranslation()

  const renderOrderSize = () => {
    if (order.type === 'buy') {
      return (
        <LimitOrderSizeBuy
          order={order}
          showSizeEquivalent={showSizeEquivalent}
        />
      )
    }

    return (
      <LimitOrderSizeSell
        order={order}
        showSizeEquivalent={showSizeEquivalent}
      />
    )
  }

  return (
    <Root>
      <Type $type={order.type}>
        {t(order.type === 'buy' ? 'trading.limit_buy' : 'trading.limit_sell')}
      </Type>
      <Amount>{renderOrderSize()}</Amount>
    </Root>
  )
}

const LimitOrderSizeBuy = ({
  order,
  showSizeEquivalent,
}: {
  order: LimitOrderBuyModel
  showSizeEquivalent: boolean
}) => {
  const [amount] = useAtom(order.purchaseAmountBase)

  const sizeInCurrency = amount.dividedBy(order.costAtCreationNative)

  return (
    <span>
      <Numeric
        value={amount}
        preset="currency-amount-short"
        currency={order.pair.base.ticker}
      />
      {showSizeEquivalent ? (
        <span>
          {' '}
          (~
          <Numeric
            value={sizeInCurrency}
            preset="currency-amount-short"
            currency={order.pair.quote.ticker}
          />
          )
        </span>
      ) : undefined}
    </span>
  )
}

const LimitOrderSizeSell = ({
  order,
  showSizeEquivalent,
}: {
  order: LimitOrderSellModel
  showSizeEquivalent: boolean
}) => {
  const [amount] = useAtom(order.sellAmountCurrency)

  const sizeInNative = amount.multipliedBy(order.costAtCreationNative)

  return (
    <span>
      <Numeric
        value={amount}
        preset="currency-amount-short"
        currency={order.pair.quote.ticker}
      />
      {showSizeEquivalent ? (
        <span>
          {' '}
          (~
          <Numeric
            value={sizeInNative}
            preset="currency-amount-short"
            currency={order.pair.base.ticker}
          />
          )
        </span>
      ) : undefined}
    </span>
  )
}

const Root = styled.div`
  display: grid;
  justify-content: start;
  grid-template-columns: auto auto;
  gap: 4px;

  font-size: 10px;
  font-style: normal;
  text-transform: uppercase;
`

const Type = styled.span<{ $type: LimitOrder.Type }>`
  color: ${({ $type }) =>
    $type === 'buy' ? 'var(--upAccent-9)' : 'var(--downAccent-9)'};

  font-weight: 700;
`

const Amount = styled.span`
  color: var(--text-9);

  font-weight: 600;
`
